import React, { PureComponent } from 'react';
import injectSheet from 'react-jss'
import cn from 'classnames'
import TransitionGroup from 'react-transition-group/TransitionGroup';
import Transition from 'react-transition-group/Transition';
import { connect } from 'react-redux'
import {TweenLite, Power2} from 'gsap'
import closeImg from '../../img/icons/close.svg'
import SVGSymbol from '../SVGSymbol'
import CentredContentContainer from '../Widgets/CentredContentContainer'
import ResponsiveImage from '../Widgets/ResponsiveImage'
import Label from '../Widgets/Label'
import {UnstyledLink} from '../Widgets/Link'
import ArticleTime from '../Article/ArticleTime'
import {searchQueryChanged, toggleSearchDialog} from '../../actions'
import {
    getSearchLoading,
    getSearchDialogIsOpen,
    getSearchQuery,
    getSearchResults,
    getTrending,
    hasMoreSearchPages,
    getTotalSearchResults,
} from "../../selectors"
import {formatSummary} from "../../utils/search"
import SearchFilter from './SearchFilter'
import {getScrollBarWidth} from '../../utils/measure'
import {BASE_URL} from '../../config'

const TRANSITION_DURATION_SECONDS = 0.3;

class SearchSummary extends PureComponent {

    _onQueryInputChanged = (e) => {
        const {query, handleSearch} = this.props;
        handleSearch( {
                ...query,
                query: e.target.value
            })
    }

    _onCategoriesChanged = (values) => {
        const {query, handleSearch} = this.props;
        handleSearch( {
            ...query,
            categories: values
        });
    }

    _onTagsChanged = (values) => {
        const {query, handleSearch} = this.props;
        handleSearch( {
            ...query,
            tags: values
        });
    }

    _toggleMen = () => {
        this._toggleGender('men');
    }

    _toggleWomen = () => {
        this._toggleGender('women');
    }

    _toggleGender = (gender) => {
        const {query, handleSearch} = this.props;
        const index = query.gender.indexOf(gender);
        if( index < 0 )
            handleSearch( {
                ...query,
                gender: [...query.gender, gender]
            });
        else
            handleSearch( {
                ...query,
                gender: [
                    ...query.gender.slice(0, index),
                    ...query.gender.slice(index + 1)
                ]});
    }

    _onEntering = (node) => {
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.marginRight = getScrollBarWidth() + 'px';
        TweenLite.fromTo(node, TRANSITION_DURATION_SECONDS, {opacity: 0}, {opacity: 1, ease: Power2.easeInOut});
    }

    _onExiting = (node) => {
        document.documentElement.style.marginRight = 0;
        document.documentElement.style.overflow = 'auto';
        TweenLite.to(node, TRANSITION_DURATION_SECONDS, {opacity: 0, ease: Power2.easeInOut, onComplete: () => {
        }});
    }

    _getSummary = () => {
        const {query, loading, totalResults} = this.props;
        return formatSummary(query, loading, totalResults);
    }

    render() {
        const {classes, open, results, handleCloseDialog, hasMore, totalResults, trending} = this.props;
        var trimmedResults = results && results.slice(0, 8);
        const seeMoreUrl = {url: `${BASE_URL}/search`};

        return (<TransitionGroup appear={true} enter={true} exit={true}>
            {open &&
            <Transition timeout={TRANSITION_DURATION_SECONDS*1000}
                        onEntering={this._onEntering}
                        onExiting={this._onExiting}>
                <div className={cn(classes.dialog)}>
                    <div className={classes.dialogToolbar}>
                        <a onClick={handleCloseDialog}><SVGSymbol icon={closeImg} /></a>
                    </div>

                    <section>
                        <CentredContentContainer size="huge">
                            <SearchFilter showSeeAllResultsLink={true}/>
                        </CentredContentContainer>
                    </section>

                    <section>
                        <CentredContentContainer size="huge">
                        <div className={classes.resultsContainer}>
                            {trimmedResults && trimmedResults.map((result, i) => <div key={result.slug} className={classes.result}>
                                <div className={classes.resultLabelContainer}>
                                    <Label text={result.category} className={cn({[classes.hide]: (i === 7 && hasMore)})}></Label>
                                    <ArticleTime article={result} trending={trending} hideArticleLength={true} />
                                </div>
                                <ResponsiveImage key={result.slug} images={{desktop: result.images}}
                                                link={result.link}
                                                 parralax={false} aspect="0.73">
                                    {i === 7 && hasMore &&
                                        <UnstyledLink link={seeMoreUrl} className={classes.resultSeeMore}><h3>See all {totalResults} results</h3></UnstyledLink>}
                                </ResponsiveImage>
                                {i === 7 && hasMore ? null : <h3 className={classes.resultTitle}>{result.title}</h3>}
                             </div>)}
                        </div>
                        </CentredContentContainer>
                    </section>
                </div>
            </Transition>}
        </TransitionGroup>)
    }
}

const mapStateToProps = (state) => {
    return {
        loading: getSearchLoading(state),
        query: getSearchQuery(state),
        open: getSearchDialogIsOpen(state),
        results: getSearchResults(state),
        trending: getTrending(state),
        hasMore: hasMoreSearchPages(state),
        totalResults: getTotalSearchResults(state),
    }
}

function mapDispatchToProps(dispatch) {
    return {
        handleSearch: ( query ) => {
            dispatch(searchQueryChanged(query))
        },
        handleCloseDialog: () => {
            dispatch(toggleSearchDialog())
        },
    }
}

export default injectSheet((theme) => ({
    dialog: {
        overflowY: "auto",
        backgroundColor: theme.colors.info,
        zIndex: theme.zIndex.searchDialog,
        color: theme.colors.black,
        position: "fixed",
        '-webkit-transform': 'translateZ(0)',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    dialogToolbar: {
        ...theme.header.height('height'),
        color: theme.colors.black,
        display: 'flex',
        padding: "0.8rem 1rem",
        width: "100%",
        alignItems: "center",
        justifyContent: "flex-end",
        [theme.breakpoint("mobile")]: {
            padding: "0.8rem 1.2rem",
        },
        '& a': {
            backgroundColor: 'transparent',
            borderWidth: 0,
            cursor: 'pointer',
            transition: 'color 0.3s ease'
        },
        '& a:hover': {
            color: theme.colors.primary
        }
    },
    resultsContainer: {
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        margin: `0 -10px`,
    },
    filter: {
        display: 'flex',
        margin: '0',
        [theme.breakpoint('tablet')]: {
            margin: '0 -10px',
        },
        flexWrap: 'wrap',
        "& > div": {
            flexBasis: '50%',
            [theme.breakpoint('tablet')]: {
                margin: '0 10px',
                flexBasis: 'auto'
            },
        }
    },
    result: {
        flexBasis: '50%',
        padding: 10,
        [theme.breakpoint('tablet')]: {
            flexBasis: '33%',
        },
        [theme.breakpoint('laptop')]: {
            flexBasis: '25%',
        },
    },
    resultLabelContainer: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    resultTitle: {
        fontStyle: 'normal',
        lineHeight: '1.5em',
        margin: '10px 0',
        textTransform: 'none',
        fontSize: '16px',
        [theme.breakpoint('mobile')]: {
            fontSize: '18px',
        },
        [theme.breakpoint('tablet')]: {
            fontSize: '22px',
        }
    },
    resultSeeMore: {
        backgroundColor: 'rgba(255, 255, 255, 0.88)',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textDecoration: 'none',
        '& h3': {
            textAlign: 'center',
            fontStyle: 'normal',
            textTransform: 'none',
            textDecoration: 'none',
            lineHeight: '1.5em',
            fontSize: '18px',
            margin: 0,
        }
    },
    hide: {
        visibility: 'hidden'
    },
    searchResultSummary: {
        ...theme.font.title.h4
    },
    summary: {
        display: 'flex',
        color: theme.colors.assent,
        '& strong': {
            color: theme.colors.black,
        },
        '& span': {
            flexGrow: 1,
            display: 'none',
            [theme.breakpoint('tablet')]: {
               display: 'block',
           }
        }
    }
}))( connect(mapStateToProps, mapDispatchToProps)(SearchSummary) );



// WEBPACK FOOTER //
// ./src/components/Search/SearchSummary.js